/**
 * @file Actions du hub social (non persistees) : zoom camera, arme rangee, feu d'artifice avec le skin de munition du chef.
 */
import { FIREWORK_COOLDOWN, HUB_CATNIP, HUB_ZOOM, seasonOf } from '@config/hub';
import type { HubSeason } from '@config/hub';
import { fail, okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import { killTarget } from '../combat/damage';
import type { SimContext } from '../Context';
import { spawnTarget } from '../ecs/EntityFactory';
import type { With } from '../ecs/World';

export class HubService {
  private lastFirework = -Infinity;
  /** Herbe a chat du serveur : identifiant reseau -> entite. */
  private readonly netCatnip = new Map<string, number>();

  constructor(private readonly ctx: SimContext) {}

  private inHub(): boolean {
    return this.ctx.modes.currentId === 'hub';
  }

  /** Remise a zero a chaque entree dans un mode (appelee par ModeManager via HubMode.onStart / onExit). */
  reset(): void {
    const hub = this.ctx.rt.hub;
    hub.zoom = 1;
    hub.stowed = false;
    hub.season = hub.seasonOverride ?? seasonOf(this.ctx.clock.nowMs());
  }

  /** Brindilles d'herbe a chat presentes dans le monde (locales ou venues du serveur). */
  private catnipEntities(): Array<With<'target'>> {
    return this.ctx.rt.world.query('target', 'target').filter((e) => e.target.defId === HUB_CATNIP.targetId);
  }

  /** Bascule l'herbe a chat entre « locale » (hors ligne) et « serveur » : les brindilles de l'autre origine disparaissent. */
  setNetOnline(online: boolean): Result {
    const ctx = this.ctx;
    const hub = ctx.rt.hub;
    if (hub.online === online) return okVoid;
    hub.online = online;
    if (online) {
      for (const e of this.catnipEntities()) ctx.rt.world.destroy(e.id);
    } else {
      for (const id of this.netCatnip.values()) ctx.rt.world.destroy(id);
    }
    this.netCatnip.clear();
    return okVoid;
  }

  /** Une brindille annoncee par le serveur (position monde). */
  netCatnipAdd(id: string, x: number, y: number): Result {
    if (!this.inHub() || !this.ctx.rt.hub.online) return fail('wrongMode');
    if (this.netCatnip.has(id)) return okVoid;
    const e = spawnTarget(this.ctx, this.ctx.index.target(HUB_CATNIP.targetId), x, y, { stageValue: 0 });
    this.netCatnip.set(id, e.id);
    return okVoid;
  }

  /** Brindille cueillie par un autre joueur (ou disparue) : retrait sans recompense. */
  netCatnipRemove(id: string): Result {
    const eid = this.netCatnip.get(id);
    if (eid === undefined) return okVoid;
    this.netCatnip.delete(id);
    this.ctx.rt.world.destroy(eid);
    return okVoid;
  }

  /** Le serveur confirme NOTRE cueillette : retrait + compteur local + evenement (effets visuels / sons habituels). */
  netCatnipCredit(id: string, gain = 1): Result {
    const ctx = this.ctx;
    const eid = this.netCatnip.get(id);
    const e = eid === undefined ? undefined : ctx.rt.world.get(eid);
    const leader = ctx.rt.catIds[0] === undefined ? undefined : ctx.rt.world.get(ctx.rt.catIds[0]);
    const at = e?.transform ?? leader?.transform ?? { x: 0, y: 0 };
    ctx.data.hub.catnip += Math.max(1, Math.min(10, Math.floor(gain)));
    ctx.bus.emit('CatnipCollected', { x: at.x, y: at.y, total: ctx.data.hub.catnip });
    return this.netCatnipRemove(id);
  }

  /** Debug : detruit la maisonnette la plus proche du chef (declenche l'animation d'ecroulement cote rendu). */
  demolishNearestHouse(): Result {
    const ctx = this.ctx;
    if (!this.inHub()) return fail('wrongMode');
    const leader = ctx.rt.catIds[0] === undefined ? undefined : ctx.rt.world.get(ctx.rt.catIds[0]);
    const from = leader?.transform ?? { x: 0, y: 0 };
    let best: With<'target' | 'health'> | null = null;
    let bestD = Infinity;
    for (const e of ctx.rt.world.query<'target' | 'health'>('target', 'target', 'health')) {
      if (e.target.defId !== 'decor_house' || (e.visual?.dying ?? 0) > 0) continue;
      const d = (e.transform.x - from.x) ** 2 + (e.transform.y - from.y) ** 2;
      if (d < bestD) {
        best = e;
        bestD = d;
      }
    }
    if (!best) return fail('invalidTarget');
    killTarget(ctx, best);
    return okVoid;
  }

  /** Debug : force la saison du decor (null = date reelle) ; prend effet a la prochaine entree dans le hub. */
  forceSeason(season: HubSeason | null): Result {
    this.ctx.rt.hub.seasonOverride = season;
    return okVoid;
  }

  zoom(action: 'in' | 'out' | 'reset'): Result {
    if (!this.inHub()) return fail('wrongMode');
    const hub = this.ctx.rt.hub;
    if (action === 'reset') hub.zoom = 1;
    else {
      const f = action === 'in' ? HUB_ZOOM.step : 1 / HUB_ZOOM.step;
      hub.zoom = Math.min(HUB_ZOOM.max, Math.max(HUB_ZOOM.min, hub.zoom * f));
    }
    return okVoid;
  }

  /** Ranger / sortir l'arme (le chef la tient a une main sur le cote). */
  stow(stowed?: boolean): Result {
    if (!this.inHub()) return fail('wrongMode');
    const hub = this.ctx.rt.hub;
    hub.stowed = stowed ?? !hub.stowed;
    return okVoid;
  }

  /** Petit feu d'artifice au-dessus du chef, avec le style de la munition equipee sur son arme. */
  firework(): Result {
    const ctx = this.ctx;
    if (!this.inHub()) return fail('wrongMode');
    if (ctx.rt.time - this.lastFirework < FIREWORK_COOLDOWN) return fail('cooldown');
    const leader = ctx.rt.catIds[0] === undefined ? undefined : ctx.rt.world.get(ctx.rt.catIds[0]);
    if (!leader) return fail('invalidTarget');
    this.lastFirework = ctx.rt.time;
    const uid = leader.cat?.weaponUid ?? null;
    const item = uid === null ? undefined : ctx.data.weapons.items.find((w) => w.uid === uid);
    const behavior = item ? ctx.index.weapon(item.defId).behavior : 'single';
    ctx.bus.emit('Firework', { x: leader.transform.x, y: leader.transform.y, style: ctx.services.ammoSkins.styleOf(behavior), show: ctx.services.fireworks.activeStyle() });
    return okVoid;
  }
}
